import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import {
  createEntityAdapter,
  createSlice,
  EntityAdapter,
  EntityId,
  PayloadAction,
} from "@reduxjs/toolkit";
import { BaseEntityState } from "@store/store-types";
import { GroupTypes } from "@custom-types/group-types";
import { GroupId } from "@custom-types/base-types";
import {
  removeLocalMemberFromGroup,
  updateMemberRoleInGroupStore,
} from "@store/groups/groups-slice-utils";
import {
  addGroup,
  fetchGroupDetails,
  fetchGroups,
  removeMemberFromGroup,
  updateGroupDetails,
  updateMemberRoleInGroup,
} from "@store/groups/groups-slice-thunk";

/**
 * State of groups
 */
export interface GroupsState extends BaseEntityState<GroupTypes> {
  /** The selected group in which user is viewing */
  selectedGroupId: GroupId | null;

  /** Collects all the fetching properties for this slice */
  fetching: {
    /** Indicates if groups are currently being fetched from the backend */
    isFetchingGroups: boolean;

    /**
     * Map of groups IDs where a process is currently ongoing for them.
     * The value is always true as we remove the entity if the group is not being processed anymore
     */
    processingGroups: { [key: EntityId]: true };

    /** Indicates if a group member is currently being removed from the backend */
    isRemovingGroupMember: boolean;

    /** Indicates if a group is currently being updated to the backend */
    isUpdatingGroups: boolean;
  };
}

/** Creates an entity adapter to store a map with all the groups that belong to the company. */
export const groupsAdapter: EntityAdapter<GroupTypes, EntityId> =
  createEntityAdapter();

const initialState: GroupsState = {
  ...groupsAdapter.getInitialState(),
  selectedGroupId: null,
  fetching: {
    isFetchingGroups: false,
    processingGroups: {},
    isRemovingGroupMember: false,
    isUpdatingGroups: false,
  },
};

/**
 * Slice to access state of loaded groups
 */
const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    /**
     * Accepts a single group entity and adds or replaces it.
     *
     * @param state store state
     * @param entity group to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setOne: groupsAdapter.setOne,
    /**
     * Accepts an array of group entities, and adds or replaces them.
     *
     * @param state store state
     * @param entity groups to be set or added to the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    setMany: groupsAdapter.setMany,
    /**
     * Removes all group entities from the store.
     *
     * @param state store state
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeAll: groupsAdapter.removeAll,
    /**
     * Accepts an array of group IDs, and removes each group entity with those IDs if they exist.
     *
     * @param state store state
     * @param entity Group Ids to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeMany: groupsAdapter.removeMany,
    /**
     * Accepts a single group IDs, and removes the group entity with that ID if it exists.
     *
     * @param state store state
     * @param entity Group Id to be removed from the store.
     *
     * @see https://redux-toolkit.js.org/api/createEntityAdapter#crud-functions
     */
    removeOne: groupsAdapter.removeOne,

    /** Adds group IDs to the processing groups map */
    addProcessingGroups(state, action: PayloadAction<GroupId[]>) {
      action.payload.forEach(
        (groupId) => (state.fetching.processingGroups[groupId] = true)
      );
    },

    /** Removes group IDs from the processing groups map */
    removeProcessingGroups(state, action: PayloadAction<GroupId[]>) {
      action.payload.forEach(
        (groupId) => delete state.fetching.processingGroups[groupId]
      );
    },

    /**
     * Stores the group ID of the selected group.
     */
    setSelectedGroupId(state, action: PayloadAction<string | null>) {
      state.selectedGroupId = action.payload;
    },

    /** Resets the group store to its initial state. */
    resetGroupsState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.fetching.isFetchingGroups = true;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.fetching.isFetchingGroups = false;
        // Using upsertMany instead of setAll as there might be some groups in shape of group details already in store
        // and setMany will unnecessary remove and replace them
        groupsAdapter.upsertMany(state, action.payload);
      })
      .addCase(fetchGroups.rejected, (state) => {
        state.fetching.isFetchingGroups = false;
      })

      .addCase(fetchGroupDetails.pending, (state) => {
        state.fetching.isFetchingGroups = true;
      })
      .addCase(fetchGroupDetails.fulfilled, (state, action) => {
        state.selectedGroupId = action.payload.id;
        groupsAdapter.setOne(state, action.payload);
        state.fetching.isFetchingGroups = false;
      })
      .addCase(fetchGroupDetails.rejected, (state) => {
        state.fetching.isFetchingGroups = false;
      })

      .addCase(addGroup.fulfilled, (state, action) => {
        groupsAdapter.setOne(state, action.payload);
      })
      .addCase(addGroup.rejected, (_, action) => {
        throw new Error(getErrorDisplayMarkup(action.error));
      })

      .addCase(removeMemberFromGroup.pending, (state) => {
        state.fetching.isRemovingGroupMember = true;
      })
      .addCase(removeMemberFromGroup.fulfilled, (state, action) => {
        removeLocalMemberFromGroup(state, action.payload);
        state.fetching.isRemovingGroupMember = false;
      })
      .addCase(removeMemberFromGroup.rejected, (state) => {
        state.fetching.isRemovingGroupMember = false;
      })

      .addCase(updateMemberRoleInGroup.pending, (state) => {
        state.fetching.isUpdatingGroups = true;
      })
      .addCase(updateMemberRoleInGroup.fulfilled, (state, action) => {
        updateMemberRoleInGroupStore(state, action.payload);
        state.fetching.isUpdatingGroups = false;
      })
      .addCase(updateMemberRoleInGroup.rejected, (state) => {
        state.fetching.isUpdatingGroups = false;
      })

      .addCase(updateGroupDetails.pending, (state) => {
        state.fetching.isUpdatingGroups = true;
      })
      .addCase(updateGroupDetails.fulfilled, (state, action) => {
        state.fetching.isUpdatingGroups = false;
        groupsAdapter.setOne(state, action.payload);
      })
      .addCase(updateGroupDetails.rejected, (state, action) => {
        state.fetching.isUpdatingGroups = false;
        throw new Error(getErrorDisplayMarkup(action.error));
      });
  },
});

export const {
  setOne,
  setMany,
  removeAll,
  removeMany,
  removeOne,
  addProcessingGroups,
  removeProcessingGroups,
  setSelectedGroupId,
  resetGroupsState,
} = groupsSlice.actions;

export const groupsReducer = groupsSlice.reducer;
